import {
  addP,
  bbox,
  computeSiteMainAxisAngleRad,
  distToPolygon,
  edgeOutwardNormal,
  insideBuildingWithMargin,
  normalizeVec,
  orientedRectCorners,
  pointInPolygon,
  polygonCentroid,
  rotP,
  scaleVec,
  subP,
  toDeg,
  toRad,
} from './geometry';
import {
  type AccessBase,
  buildAccessBase,
  computeAccess,
  openCorridors,
  planCorridor,
} from './parking/access';
import { PARKING_METHOD_KEYS } from './parking/constants';
import type {
  BicycleState,
  Building,
  CirculationWarning,
  DrivewayConnector,
  FreeGrid,
  GridLane,
  GridStall,
  OrientedRect,
  PackedResult,
  ParkingAngleMode,
  ParkingLane,
  ParkingMethodKey,
  ParkingMethodState,
  ParkingSlot,
  ParkingState,
  Point,
  RowsAlongXMode,
  SiteEdge,
  SiteState,
  StallType,
} from './types';

// Pure (state-free) parts of the parking/bicycle auto-layout engine —
// migration plan Phase 3. Functions that read the global `state` directly
// (buildFreeGrid, isCellValidRoadDirect, packStallsRoadDirect,
// packStallsWallBacked, solveLayoutAngle, recomputeParking, etc.) are ported
// separately in Phase 4 alongside the de-globalization work, since
// isCellValidRoadDirect's existing state.buildings reference must be fixed
// to take buildings explicitly at the same time it's ported (see the
// migration plan's "Finding A/B").

// 直角駐車(90度)の簡易的な最小通路幅の目安: 2×回転半径-マス奥行き(切り返し無しで一発転回できる目安。実際は車種・仕様により異なる)
export function computeRecommendedAisleWidth(turnRadius: number, stallD: number): number {
  return Math.max(3.5, 2 * (turnRadius || 6.0) - (stallD || 5.0));
}

export function rebuildPrefixSum(grid: FreeGrid): void {
  const { nx, ny, free, prefix } = grid;
  for (let iy = 0; iy < ny; iy++) {
    for (let ix = 0; ix < nx; ix++) {
      const v = free[iy * nx + ix];
      prefix[(iy + 1) * (nx + 1) + (ix + 1)] =
        v +
        prefix[iy * (nx + 1) + (ix + 1)] +
        prefix[(iy + 1) * (nx + 1) + ix] -
        prefix[iy * (nx + 1) + ix];
    }
  }
}

export function isRectFree(
  grid: FreeGrid,
  x0: number,
  y0: number,
  x1: number,
  y1: number
): boolean {
  const { bb, cellSize, nx, ny, prefix } = grid;
  const ix0 = Math.max(0, Math.floor((x0 - bb.minX) / cellSize));
  const iy0 = Math.max(0, Math.floor((y0 - bb.minY) / cellSize));
  const ix1 = Math.min(nx, Math.ceil((x1 - bb.minX) / cellSize));
  const iy1 = Math.min(ny, Math.ceil((y1 - bb.minY) / cellSize));
  if (ix0 >= ix1 || iy0 >= iy1) return false;
  const total = (ix1 - ix0) * (iy1 - iy0);
  const sum =
    prefix[iy1 * (nx + 1) + ix1] -
    prefix[iy0 * (nx + 1) + ix1] -
    prefix[iy1 * (nx + 1) + ix0] +
    prefix[iy0 * (nx + 1) + ix0];
  return sum === total;
}

// 車が通行できる(通路にできる)範囲かどうか。passPrefixを持たないグリッドでは判定しない(常にtrue)
export function isRectPassable(
  grid: FreeGrid,
  x0: number,
  y0: number,
  x1: number,
  y1: number
): boolean {
  const { bb, cellSize, nx, ny, passPrefix } = grid;
  if (!passPrefix) return true;
  const ix0 = Math.floor((x0 - bb.minX) / cellSize + 1e-6);
  const iy0 = Math.floor((y0 - bb.minY) / cellSize + 1e-6);
  const ix1 = Math.ceil((x1 - bb.minX) / cellSize - 1e-6);
  const iy1 = Math.ceil((y1 - bb.minY) / cellSize - 1e-6);
  // グリッド(=敷地の外接矩形)の外にはみ出す通路は敷地外なので通行不可
  if (ix0 < 0 || iy0 < 0 || ix1 > nx || iy1 > ny) return false;
  if (ix0 >= ix1 || iy0 >= iy1) return false;
  const total = (ix1 - ix0) * (iy1 - iy0);
  const sum =
    passPrefix[iy1 * (nx + 1) + ix1] -
    passPrefix[iy0 * (nx + 1) + ix1] -
    passPrefix[iy1 * (nx + 1) + ix0] +
    passPrefix[iy0 * (nx + 1) + ix0];
  return sum === total;
}

export function markRectOccupied(
  grid: FreeGrid,
  x0: number,
  y0: number,
  x1: number,
  y1: number
): void {
  const { bb, cellSize, nx, ny, free } = grid;
  const ix0 = Math.max(0, Math.floor((x0 - bb.minX) / cellSize));
  const iy0 = Math.max(0, Math.floor((y0 - bb.minY) / cellSize));
  const ix1 = Math.min(nx, Math.ceil((x1 - bb.minX) / cellSize));
  const iy1 = Math.min(ny, Math.ceil((y1 - bb.minY) / cellSize));
  for (let iy = iy0; iy < iy1; iy++) {
    for (let ix = ix0; ix < ix1; ix++) free[iy * nx + ix] = 0;
  }
}

// 列に沿った位置(各マスの始点)を、隙間なく連続する区間 [始点, 終点] ごとにまとめる
export function contiguousRuns(starts: number[], stallW: number): [number, number][] {
  const sorted = [...starts].sort((a, b) => a - b);
  const runs: [number, number][] = [];
  for (const u of sorted) {
    const last = runs[runs.length - 1];
    if (last && Math.abs(u - last[1]) < 1e-3) last[1] = u + stallW;
    else runs.push([u, u + stallW]);
  }
  return runs;
}

/* --- 背合わせ配置: 通路|マスA|マスB(背中合わせ)|通路 の繰り返し --- */
export function packStallsBackToBackDirection(
  grid: FreeGrid,
  stallW: number,
  stallD: number,
  aisleW: number,
  rowsAlongX: boolean,
  shiftToFit = false
): PackedResult {
  const { bb } = grid;
  const stalls: GridStall[] = [];
  const lanes: GridLane[] = [];
  const pairDepth = stallD * 2;
  const compactStep = grid.cellSize || 0.5;
  // u = 列に沿う方向、v = 列を横切る方向。rowsAlongX=trueなら(u,v)=(x,y)、falseなら(y,x)
  const uMin = rowsAlongX ? bb.minX : bb.minY;
  const uMax = rowsAlongX ? bb.maxX : bb.maxY;
  const vMin = rowsAlongX ? bb.minY : bb.minX;
  const vMax = rowsAlongX ? bb.maxY : bb.maxX;
  const makeStall = (u: number, v: number): GridStall =>
    rowsAlongX
      ? { x0: u, y0: v, x1: u + stallW, y1: v + stallD, type: 'normal' }
      : { x0: v, y0: u, x1: v + stallD, y1: u + stallW, type: 'normal' };
  // 各列の前(手前側の列は帯の手前、奥側の列は帯の奥)に通路を取れる範囲かどうか
  const aislePassable = (u: number, a0: number, a1: number): boolean =>
    rowsAlongX
      ? isRectPassable(grid, u, a0, u + stallW, a1)
      : isRectPassable(grid, a0, u, a1, u + stallW);
  const collectBand = (v: number): GridStall[] => {
    const band: GridStall[] = [];
    for (const [rowV, a0, a1] of [
      [v, v - aisleW, v],
      [v + stallD, v + pairDepth, v + pairDepth + aisleW],
    ]) {
      for (let u = uMin; u + stallW <= uMax; u += stallW) {
        const st = makeStall(u, rowV);
        if (isRectFree(grid, st.x0, st.y0, st.x1, st.y1) && aislePassable(u, a0, a1)) band.push(st);
      }
    }
    return band;
  };
  let v = vMin + aisleW;
  while (v + pairDepth <= vMax) {
    let bandV = v;
    let laneStalls = collectBand(v);
    if (shiftToFit && laneStalls.length > 0) {
      // 片側の列しか置けない位置で確定させず、マス奥行きの範囲で少し先へずらして両列が入る位置を探す
      // (壁背負い列の通路と自前の通路が二重になり、通路2本分の空きが残るのを防ぐ)
      for (let dv = compactStep; dv <= stallD + 1e-9; dv += compactStep) {
        if (v + dv + pairDepth > vMax) break;
        const shifted = collectBand(v + dv);
        if (shifted.length > laneStalls.length) {
          laneStalls = shifted;
          bandV = v + dv;
        }
      }
    }
    if (laneStalls.length > 0) {
      stalls.push(...laneStalls);
      const us = laneStalls.flatMap((st) => (rowsAlongX ? [st.x0, st.x1] : [st.y0, st.y1]));
      const uLo = Math.min(...us);
      const uHi = Math.max(...us);
      // 手前の通路が必要なのは手前側の列(通路に面する列)がある範囲だけ。奥側の列は次の通路に面する
      // 手前側の列が途切れる箇所(建物や壁背負い列の手前など)では通路も途切れるため、連続区間ごとに分ける
      const aisleRuns = contiguousRuns(
        laneStalls
          .filter((st) => Math.abs((rowsAlongX ? st.y0 : st.x0) - bandV) < 1e-6)
          .map((st) => (rowsAlongX ? st.x0 : st.y0)),
        stallW
      );
      const backAisleRuns = contiguousRuns(
        laneStalls
          .filter((st) => Math.abs((rowsAlongX ? st.y0 : st.x0) - (bandV + stallD)) < 1e-6)
          .map((st) => (rowsAlongX ? st.x0 : st.y0)),
        stallW
      );
      lanes.push(
        rowsAlongX
          ? {
              axis: 'x',
              laneType: 'pair',
              y0: bandV,
              y1: bandV + pairDepth,
              x0: uLo,
              x1: uHi,
              aisleW,
              aisleRuns,
              backAisleRuns,
            }
          : {
              axis: 'y',
              laneType: 'pair',
              x0: bandV,
              x1: bandV + pairDepth,
              y0: uLo,
              y1: uHi,
              aisleW,
              aisleRuns,
              backAisleRuns,
            }
      );
      v = bandV + pairDepth + aisleW;
    } else {
      // この帯には1台も置けなかった(壁背負い帯等が広く食い込んでいる)。固定ピッチで進めると
      // その帯の分だけ丸ごと無駄な空間になるため、細かいステップで次に置ける位置まで詰める
      v += compactStep;
    }
  }
  return { stalls, lanes, rowsAlongX };
}
export function packStallsBackToBack(
  grid: FreeGrid,
  stallW: number,
  stallD: number,
  aisleW: number,
  rowsAlongXOverride: boolean | undefined
): PackedResult {
  const directions = rowsAlongXOverride === undefined ? [true, false] : [rowsAlongXOverride];
  let best: PackedResult | null = null;
  // 帯をずらす/ずらさないの両方を試し、台数の多い方を採用する(同数なら従来どおりずらさない方・行方向を優先)
  for (const rowsAlongX of directions) {
    for (const shiftToFit of [false, true]) {
      const r = packStallsBackToBackDirection(grid, stallW, stallD, aisleW, rowsAlongX, shiftToFit);
      if (!best || r.stalls.length > best.stalls.length) best = r;
    }
  }
  return best as PackedResult;
}

/* --- 縦列駐車(タンデム): 1区画=奥行きstallD*2、前後2スロットに分割 --- */
let tandemGroupSeq = 0;
export function packStallsTandemDirection(
  grid: FreeGrid,
  stallW: number,
  stallD: number,
  aisleW: number,
  rowsAlongX: boolean
): PackedResult {
  const { bb } = grid;
  const stalls: GridStall[] = [];
  const lanes: GridLane[] = [];
  const unitD = stallD * 2;
  if (rowsAlongX) {
    for (let y = bb.minY; y + unitD <= bb.maxY; y += unitD + aisleW) {
      const laneStalls: GridStall[] = [];
      for (let x = bb.minX; x + stallW <= bb.maxX; x += stallW) {
        if (isRectFree(grid, x, y, x + stallW, y + unitD)) {
          const groupId = ++tandemGroupSeq;
          const front: GridStall = {
            x0: x,
            y0: y,
            x1: x + stallW,
            y1: y + stallD,
            type: 'normal',
            tandemGroupId: groupId,
            tandemPosition: 'front',
          };
          const back: GridStall = {
            x0: x,
            y0: y + stallD,
            x1: x + stallW,
            y1: y + unitD,
            type: 'normal',
            tandemGroupId: groupId,
            tandemPosition: 'back',
          };
          stalls.push(front, back);
          laneStalls.push(front);
        }
      }
      if (laneStalls.length > 0) {
        lanes.push({
          axis: 'x',
          laneType: 'single',
          y0: y,
          y1: y + unitD,
          x0: laneStalls[0].x0,
          x1: laneStalls[laneStalls.length - 1].x1,
          aisleW,
        });
      }
    }
  } else {
    for (let x = bb.minX; x + unitD <= bb.maxX; x += unitD + aisleW) {
      const laneStalls: GridStall[] = [];
      for (let y = bb.minY; y + stallW <= bb.maxY; y += stallW) {
        if (isRectFree(grid, x, y, x + unitD, y + stallW)) {
          const groupId = ++tandemGroupSeq;
          const front: GridStall = {
            x0: x,
            y0: y,
            x1: x + stallD,
            y1: y + stallW,
            type: 'normal',
            tandemGroupId: groupId,
            tandemPosition: 'front',
          };
          const back: GridStall = {
            x0: x + stallD,
            y0: y,
            x1: x + unitD,
            y1: y + stallW,
            type: 'normal',
            tandemGroupId: groupId,
            tandemPosition: 'back',
          };
          stalls.push(front, back);
          laneStalls.push(front);
        }
      }
      if (laneStalls.length > 0) {
        lanes.push({
          axis: 'y',
          laneType: 'single',
          x0: x,
          x1: x + unitD,
          y0: laneStalls[0].y0,
          y1: laneStalls[laneStalls.length - 1].y1,
          aisleW,
        });
      }
    }
  }
  return { stalls, lanes, rowsAlongX };
}
export function packStallsTandem(
  grid: FreeGrid,
  stallW: number,
  stallD: number,
  aisleW: number,
  rowsAlongXOverride: boolean | undefined
): PackedResult {
  if (rowsAlongXOverride === true)
    return packStallsTandemDirection(grid, stallW, stallD, aisleW, true);
  if (rowsAlongXOverride === false)
    return packStallsTandemDirection(grid, stallW, stallD, aisleW, false);
  const a = packStallsTandemDirection(grid, stallW, stallD, aisleW, true);
  const b = packStallsTandemDirection(grid, stallW, stallD, aisleW, false);
  return a.stalls.length >= b.stalls.length ? a : b;
}

export function assignSpecialSlots(
  slots: ParkingSlot[],
  counts: { disabled: number; guest: number; ev: number }
): void {
  let idx = 0;
  const types: StallType[] = ['disabled', 'guest', 'ev'];
  types.forEach((type) => {
    const n = counts[type as 'disabled' | 'guest' | 'ev'] || 0;
    for (let i = 0; i < n && idx < slots.length; i++, idx++) {
      slots[idx].type = type;
    }
  });
}

export function findMaxFreeRect(
  grid: FreeGrid
): { x0: number; y0: number; x1: number; y1: number } | null {
  const { bb, cellSize, nx, ny, free } = grid;
  const heights = new Int32Array(nx);
  let best: { x0i: number; x1i: number; y1i: number; heightCells: number } | null = null;
  let bestArea = 0;
  for (let iy = 0; iy < ny; iy++) {
    for (let ix = 0; ix < nx; ix++) heights[ix] = free[iy * nx + ix] === 1 ? heights[ix] + 1 : 0;
    const stack: number[] = [];
    for (let ix = 0; ix <= nx; ix++) {
      const h = ix < nx ? heights[ix] : 0;
      while (stack.length && heights[stack[stack.length - 1]] >= h) {
        const top = stack.pop() as number;
        const height = heights[top];
        const width = stack.length ? ix - stack[stack.length - 1] - 1 : ix;
        const area = height * width;
        if (area > bestArea) {
          bestArea = area;
          const x0i = stack.length ? stack[stack.length - 1] + 1 : 0;
          best = { x0i, x1i: ix, y1i: iy + 1, heightCells: height };
        }
      }
      stack.push(ix);
    }
  }
  if (!best) return null;
  const y0i = best.y1i - best.heightCells;
  return {
    x0: bb.minX + best.x0i * cellSize,
    x1: bb.minX + best.x1i * cellSize,
    y0: bb.minY + y0i * cellSize,
    y1: bb.minY + best.y1i * cellSize,
  };
}

/* --- 角度ソルバー: ローカル(グリッド)座標 -> ワールド座標+角度への変換 --- */
export function localAxisRectToWorldOriented(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  gridAngleRad: number,
  extraAngle: number
): OrientedRect {
  const localCenter: Point = { x: (x0 + x1) / 2, y: (y0 + y1) / 2 };
  return {
    center: rotP(localCenter, gridAngleRad),
    width: x1 - x0,
    depth: y1 - y0,
    angle: gridAngleRad + (extraAngle || 0),
  };
}
export function localSlotToWorldOriented(
  s: GridStall,
  gridAngleRad: number,
  extraAngle: number,
  rowsAlongX: boolean
): ParkingSlot {
  const r = localAxisRectToWorldOriented(s.x0, s.y0, s.x1, s.y1, gridAngleRad, extraAngle);
  // rowsAlongX=falseの場合、ローカルのx1-x0/y1-y0は「奥行き/幅」が入れ替わっているため補正する(localLaneToWorldOrientedと同じ理由)
  const width = rowsAlongX === false ? r.depth : r.width;
  const depth = rowsAlongX === false ? r.width : r.depth;
  return {
    center: r.center,
    width,
    depth,
    angle: r.angle,
    type: s.type,
    tandemGroupId: s.tandemGroupId != null ? s.tandemGroupId : null,
    tandemPosition: s.tandemPosition != null ? s.tandemPosition : null,
    roadEdgeIndex: s.roadEdgeIndex != null ? s.roadEdgeIndex : null,
  };
}
export function localLaneToWorldOriented(
  lane: GridLane,
  gridAngleRad: number,
  extraAngle: number,
  rowsAlongX: boolean
): ParkingLane {
  const r = localAxisRectToWorldOriented(
    lane.x0,
    lane.y0,
    lane.x1,
    lane.y1,
    gridAngleRad,
    extraAngle
  );
  // rowsAlongX=falseの場合、ローカルのx1-x0/y1-y0は「奥行き/長さ」が入れ替わっているため補正する
  const width = rowsAlongX ? r.width : r.depth;
  const depth = rowsAlongX ? r.depth : r.width;
  const result: ParkingLane = {
    center: r.center,
    width,
    depth,
    angle: r.angle,
    aisleW: lane.aisleW,
    laneType: lane.laneType || 'single',
  };
  if (lane.laneType === 'pair' && lane.aisleRuns) {
    // 通路帯を、各列(手前側は帯の手前aisleW、奥側は帯の奥aisleW)の連続区間ごとに作る
    const v0 = rowsAlongX ? lane.y0 : lane.x0;
    const v1 = rowsAlongX ? lane.y1 : lane.x1;
    const toWorld = ([u0, u1]: [number, number], a0: number, a1: number): OrientedRect => {
      const a = rowsAlongX
        ? localAxisRectToWorldOriented(u0, a0, u1, a1, gridAngleRad, extraAngle)
        : localAxisRectToWorldOriented(a0, u0, a1, u1, gridAngleRad, extraAngle);
      return {
        center: a.center,
        width: rowsAlongX ? a.width : a.depth,
        depth: rowsAlongX ? a.depth : a.width,
        angle: a.angle,
      };
    };
    result.aisles = [
      ...lane.aisleRuns.map((run) => toWorld(run, v0 - lane.aisleW, v0)),
      ...(lane.backAisleRuns || []).map((run) => toWorld(run, v1, v1 + lane.aisleW)),
    ];
  }
  return result;
}

export const PARKING_ANGLE_OFFSETS_DEG = [0, 30, 45, 60, 90];
// 自動探索の候補角度: 敷地の各辺の向きに揃う角度を優先し、敷地境界と整合したレイアウトになるようにする
// (辺の向きと無関係な角度を許すと、台数はわずかに増えても敷地に対して斜めにずれた見た目になるため)
export function computeEdgeAlignedOffsets(pts: Point[], baseAngleRad: number): number[] {
  if (pts.length < 3) return PARKING_ANGLE_OFFSETS_DEG;
  const angles: number[] = [];
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    if (Math.hypot(p2.x - p1.x, p2.y - p1.y) < 0.5) continue;
    const edgeAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    let offsetDeg = toDeg(edgeAngle) - toDeg(baseAngleRad);
    offsetDeg = ((offsetDeg % 180) + 180) % 180;
    if (!angles.some((a) => Math.abs(a - offsetDeg) < 3 || Math.abs(a - offsetDeg) > 177))
      angles.push(offsetDeg);
  }
  if (!angles.some((a) => a < 3 || a > 177)) angles.push(0);
  return angles;
}
// 台数を最優先しつつ、僅差(1台差以内)なら敷地の辺に揃う角度を優先して見た目のズレを避ける
export const PARKING_AUTO_ANGLE_SWEEP_DEG = Array.from({ length: 12 }, (_, i) => i * 15);

/* --- 循環動線チェック: 出入口から各通路(レーン)まで、他の通路を辿って到達できるかを判定する --- */
export const CIRCULATION_TOUCH_TOLERANCE_M = 0.6;
// rectsTouch(サンプル点+許容誤差)は「隣接/接続している」ことの判定用。
// rectsOverlapSAT(下記、許容誤差なしの正確な判定)は「重なっている(=不正)」ことの判定用に使い分ける。
// 前者を重なり判定に流用すると、通路同士が接している正常な配置まで誤検出してしまう
export function rectSamplePoints(r: OrientedRect): Point[] {
  const w = r.width / 2;
  const d = r.depth / 2;
  const local = [
    { x: -w, y: -d },
    { x: w, y: -d },
    { x: w, y: d },
    { x: -w, y: d },
    { x: 0, y: -d },
    { x: 0, y: d },
    { x: -w, y: 0 },
    { x: w, y: 0 },
    { x: 0, y: 0 },
  ];
  return local.map((p) => addP(rotP(p, r.angle), r.center));
}
export function rectsTouch(a: OrientedRect, b: OrientedRect, tolerance: number): boolean {
  for (const p of rectSamplePoints(a)) {
    if (insideBuildingWithMargin(p, b, tolerance)) return true;
  }
  for (const p of rectSamplePoints(b)) {
    if (insideBuildingWithMargin(p, a, tolerance)) return true;
  }
  return false;
}
// 分離軸定理(SAT)による矩形同士の正確な重なり判定。閾値0.01は浮動小数点誤差の吸収用で、
// 単に接しているだけの矩形(誤差程度の重なり)は重なりとみなさない
export function rectsOverlapSAT(a: OrientedRect, b: OrientedRect): boolean {
  const ca = orientedRectCorners(a);
  const cb = orientedRectCorners(b);
  const axes: Point[] = [];
  [ca, cb].forEach((c) => {
    for (let i = 0; i < 4; i++) {
      const p1 = c[i];
      const p2 = c[(i + 1) % 4];
      const e = { x: p2.x - p1.x, y: p2.y - p1.y };
      const n = { x: -e.y, y: e.x };
      const len = Math.hypot(n.x, n.y) || 1;
      axes.push({ x: n.x / len, y: n.y / len });
    }
  });
  for (const ax of axes) {
    const pa = ca.map((p) => p.x * ax.x + p.y * ax.y);
    const pb = cb.map((p) => p.x * ax.x + p.y * ax.y);
    if (Math.max(...pa) <= Math.min(...pb) + 1e-6 || Math.max(...pb) <= Math.min(...pa) + 1e-6)
      return false;
  }
  return true;
}
export function computeCirculationWarning(
  lanes: ParkingLane[],
  connectors: DrivewayConnector[],
  slots: ParkingSlot[]
): CirculationWarning {
  if (!lanes.length || !connectors.length)
    return { disconnectedLaneIndices: [], disconnectedCount: 0 };
  const n = lanes.length;
  const reached = new Array(n).fill(false);
  const queue: number[] = [];
  // 車が走るのは通路なので、つながりはマス帯だけでなく手前の通路まで含めた範囲同士で判定する
  // (角で直交する列同士はマス帯が接していなくても、通路同士がつながっていれば行き来できる)
  const reach = lanes.map(laneToOccupiedRect);
  lanes.forEach((_, i) => {
    if (connectors.some((c) => rectsTouch(reach[i], c, CIRCULATION_TOUCH_TOLERANCE_M))) {
      reached[i] = true;
      queue.push(i);
    }
  });
  while (queue.length) {
    const i = queue.pop() as number;
    lanes.forEach((_, j) => {
      if (!reached[j] && i !== j && rectsTouch(reach[i], reach[j], CIRCULATION_TOUCH_TOLERANCE_M)) {
        reached[j] = true;
        queue.push(j);
      }
    });
  }
  const disconnectedLaneIndices = lanes.map((_, i) => i).filter((i) => !reached[i]);
  const disconnectedCount =
    disconnectedLaneIndices.length === 0
      ? 0
      : slots.filter((s) =>
          disconnectedLaneIndices.some((li) => insideBuildingWithMargin(s.center, lanes[li], 0.3))
        ).length;
  return { disconnectedLaneIndices, disconnectedCount };
}

// レーン(マス列+通路)を、他方式の配置除外に使える1つの矩形に変換する。
// lane.depthはマス自体の奥行きのみを表すため、実際に専有する範囲にするには通路幅を加算する必要があり、
// 加算量は列の種類で異なる: wall(壁背負い1列)は手前に通路1本分、pair(背合わせ2列)は両側に通路2本分、
// single(縦列など1列で両側に通路がある形式)も通路2本分
export function laneToOccupiedRect(lane: ParkingLane): OrientedRect {
  if (lane.laneType === 'wall') {
    // wallは壁側に通路がないため、centerを通路幅の半分だけ内側(inward)にずらしてから奥行きを広げる
    const inward = lane.inward as Point;
    const center = addP(lane.center, scaleVec(inward, lane.aisleW / 2));
    return { center, width: lane.width, depth: lane.depth + lane.aisleW, angle: lane.angle };
  }
  const extraDepth = lane.laneType === 'pair' ? lane.aisleW * 2 : lane.aisleW;
  return {
    center: lane.center,
    width: lane.width,
    depth: lane.depth + extraDepth,
    angle: lane.angle,
  };
}

// レーン手前の通路帯の矩形群(地図上の通路表示と同じ位置)。pair/wall以外は通路帯を持たない
export function laneAisleRects(lane: ParkingLane): OrientedRect[] {
  if (lane.aisles) return lane.aisles;
  if (lane.laneType !== 'pair' && lane.laneType !== 'wall') return [];
  const center =
    lane.laneType === 'wall'
      ? addP(lane.center, scaleVec(lane.inward as Point, lane.depth / 2 + lane.aisleW / 2))
      : addP(lane.center, rotP({ x: 0, y: -(lane.depth / 2 + lane.aisleW / 2) }, lane.angle));
  return [{ center, width: lane.width, depth: lane.aisleW, angle: lane.angle }];
}

// =============================================================================
// De-globalized engine (migration plan Phase 4) — the functions below used to
// close over a module-level global `state` in index.html. They now take an
// explicit ParkingContext (or the top-level ParkingEngineInput) instead.
//
// isCellValidRoadDirect previously read `state.buildings` directly even
// though it already took `sitePts`/`buildingClearance` as parameters — a
// latent double-source-of-truth that was harmless only because its one
// caller always passed `buildingsList === state.buildings`. It now reads
// ctx.buildings like everything else here, closing that gap.
// =============================================================================

/** Parameters the packing algorithms need, threaded explicitly instead of closing over global state. */
export interface ParkingContext {
  sitePts: Point[];
  siteEdges: SiteEdge[];
  buildings: Building[];
  stallW: number;
  stallD: number;
  aisleW: number;
  aisleMinW: number;
  siteClearance: number;
  buildingClearance: number;
  cellSize: number;
  angleMode: ParkingAngleMode;
  manualAngleDeg: number;
  rowsAlongXMode: RowsAlongXMode;
  roadDirectEdgeIndices: number[] | null;
}

function buildContext(input: ParkingEngineInput): ParkingContext {
  return {
    sitePts: input.site.points,
    siteEdges: input.site.edges,
    buildings: input.buildings,
    stallW: input.parking.stallW,
    stallD: input.parking.stallD,
    aisleW: input.parking.aisleW,
    aisleMinW: input.parking.aisleMinW,
    siteClearance: input.parking.siteClearance,
    buildingClearance: input.parking.buildingClearance,
    cellSize: input.parking.cellSize,
    angleMode: input.parking.angleMode,
    manualAngleDeg: input.parking.manualAngleDeg,
    rowsAlongXMode: input.parking.rowsAlongXMode,
    roadDirectEdgeIndices: input.parking.roadDirectEdgeIndices,
  };
}

const PARKING_MAX_CELLS = 40000;

export interface BuildFreeGridOpts {
  restrictRect?: OrientedRect | null;
  excludeRects?: OrientedRect[];
  /** 与えると通行可能マスク(passPrefix)も作る。通路をふさぐ障害物(他の列のマスなど)。建物・敷地外は常に通行不可 */
  hardRects?: OrientedRect[];
}
export function buildFreeGrid(
  ctx: ParkingContext,
  angleRadIn: number,
  opts: BuildFreeGridOpts = {}
): FreeGrid {
  const restrictRect = opts.restrictRect || null;
  const excludeRects = opts.excludeRects || [];
  const angleRad = angleRadIn || 0;
  const siteLocalPts = ctx.sitePts.map((p) => rotP(p, -angleRad));
  let bb = bbox(siteLocalPts);
  if (restrictRect) {
    const rbb = bbox(orientedRectCorners(restrictRect).map((p) => rotP(p, -angleRad)));
    bb = {
      minX: Math.max(bb.minX, rbb.minX),
      minY: Math.max(bb.minY, rbb.minY),
      maxX: Math.min(bb.maxX, rbb.maxX),
      maxY: Math.min(bb.maxY, rbb.maxY),
    };
  }
  let cellSize = ctx.cellSize || 0.5;
  if (bb.minX >= bb.maxX || bb.minY >= bb.maxY) {
    return {
      bb,
      cellSize,
      nx: 0,
      ny: 0,
      free: new Uint8Array(0),
      prefix: new Int32Array(1),
      angleRad,
    };
  }
  let nx = Math.max(1, Math.ceil((bb.maxX - bb.minX) / cellSize));
  let ny = Math.max(1, Math.ceil((bb.maxY - bb.minY) / cellSize));
  if (nx * ny > PARKING_MAX_CELLS) {
    const scale = Math.sqrt((nx * ny) / PARKING_MAX_CELLS);
    cellSize *= scale;
    nx = Math.max(1, Math.ceil((bb.maxX - bb.minX) / cellSize));
    ny = Math.max(1, Math.ceil((bb.maxY - bb.minY) / cellSize));
  }
  const free = new Uint8Array(nx * ny);
  const hardRects = opts.hardRects;
  const pass = hardRects ? new Uint8Array(nx * ny) : null;
  const siteClearance = ctx.siteClearance || 0;
  const buildingClearance = ctx.buildingClearance || 0;
  for (let iy = 0; iy < ny; iy++) {
    for (let ix = 0; ix < nx; ix++) {
      const localPt = { x: bb.minX + (ix + 0.5) * cellSize, y: bb.minY + (iy + 0.5) * cellSize };
      const worldPt = rotP(localPt, angleRad);
      const inSite = pointInPolygon(worldPt, ctx.sitePts);
      let ok = inSite;
      if (pass) {
        let p = inSite;
        if (p)
          for (const b of ctx.buildings)
            if (insideBuildingWithMargin(worldPt, b, 0)) {
              p = false;
              break;
            }
        pass[iy * nx + ix] = p ? 1 : 0;
      }
      if (ok && siteClearance > 0 && distToPolygon(worldPt, ctx.sitePts) < siteClearance)
        ok = false;
      if (ok && restrictRect && !insideBuildingWithMargin(worldPt, restrictRect, 0)) ok = false;
      if (ok) {
        for (const b of ctx.buildings) {
          if (insideBuildingWithMargin(worldPt, b, buildingClearance)) {
            ok = false;
            break;
          }
        }
      }
      if (ok) {
        for (const ex of excludeRects) {
          // 余白はセル半分(=セルが除外矩形に掛かるかどうかの境界)。セル1つ分だと除外矩形の外側に
          // 余分な0.5mの帯ができ、壁背負い列の通路の先に不要な空き(通路の二重化)を生むため
          if (insideBuildingWithMargin(worldPt, ex, cellSize / 2 - 1e-3)) {
            ok = false;
            break;
          }
        }
      }
      free[iy * nx + ix] = ok ? 1 : 0;
    }
  }
  const grid: FreeGrid = {
    bb,
    cellSize,
    nx,
    ny,
    free,
    prefix: new Int32Array((nx + 1) * (ny + 1)),
    angleRad,
  };
  rebuildPrefixSum(grid);
  if (pass && hardRects) {
    // 障害物ごとに、その外接範囲のセルだけを調べて通行不可にする(全セル×全障害物の総当たりを避ける)
    const margin = cellSize / 2 - 1e-3;
    for (const h of hardRects) {
      const lc = orientedRectCorners(h).map((p) => rotP(p, -angleRad));
      const hb = bbox(lc);
      const ix0 = Math.max(0, Math.floor((hb.minX - margin - bb.minX) / cellSize));
      const ix1 = Math.min(nx - 1, Math.floor((hb.maxX + margin - bb.minX) / cellSize));
      const iy0 = Math.max(0, Math.floor((hb.minY - margin - bb.minY) / cellSize));
      const iy1 = Math.min(ny - 1, Math.floor((hb.maxY + margin - bb.minY) / cellSize));
      for (let iy = iy0; iy <= iy1; iy++) {
        for (let ix = ix0; ix <= ix1; ix++) {
          if (!pass[iy * nx + ix]) continue;
          const lp = { x: bb.minX + (ix + 0.5) * cellSize, y: bb.minY + (iy + 0.5) * cellSize };
          if (insideBuildingWithMargin(rotP(lp, angleRad), h, margin)) pass[iy * nx + ix] = 0;
        }
      }
    }
    grid.passPrefix = new Int32Array((nx + 1) * (ny + 1));
    rebuildPrefixSum({ ...grid, free: pass, prefix: grid.passPrefix });
  }
  return grid;
}

/* --- 道路直接駐車: 通路を作らず、道路辺に直接面してマスを配置(狭い敷地向け) --- */
const MAX_ROAD_DIRECT_SLOTS = 500;
export function isCellValidRoadDirect(
  ctx: ParkingContext,
  candidate: OrientedRect,
  existing: OrientedRect[],
  zone: OrientedRect | null,
  excludeRects: OrientedRect[]
): boolean {
  const corners = orientedRectCorners(candidate);
  for (const c of corners) {
    if (!pointInPolygon(c, ctx.sitePts)) return false;
  }
  for (const b of ctx.buildings) {
    for (const c of corners) {
      if (insideBuildingWithMargin(c, b, ctx.buildingClearance)) return false;
    }
  }
  if (zone) {
    for (const c of corners) {
      if (!insideBuildingWithMargin(c, zone, 0)) return false;
    }
  }
  if (excludeRects) {
    for (const ex of excludeRects) {
      for (const c of corners) {
        if (insideBuildingWithMargin(c, ex, 0)) return false;
      }
    }
  }
  // 既存マスとの重なりは正確な矩形重なり判定(SAT)で行う。中心間距離のしきい値方式だと、
  // 同じ壁沿いに隙間なく並ぶ隣接マス(中心間隔=マス幅)まで「近すぎる」と誤判定してしまい、
  // 壁背負い列が1台おきに間引かれる不具合になっていた
  for (const ex of existing) {
    if (rectsOverlapSAT(candidate, ex)) return false;
  }
  return true;
}
export function packStallsRoadDirect(
  ctx: ParkingContext,
  zone: OrientedRect | null,
  excludeRects: OrientedRect[]
): ParkingSlot[] {
  const pts = ctx.sitePts;
  const edges = ctx.siteEdges;
  if (pts.length < 3) return [];
  const centroid = polygonCentroid(pts);
  const targetIndices = Array.isArray(ctx.roadDirectEdgeIndices)
    ? ctx.roadDirectEdgeIndices
    : pts.map((_, i) => i).filter((i) => edges[i] && edges[i].type === 'road');
  const stallW = ctx.stallW;
  const stallD = ctx.stallD;
  const clearance = ctx.siteClearance || 0;
  const results: ParkingSlot[] = [];
  targetIndices.forEach((i) => {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    const edgeVec = subP(p2, p1);
    const edgeLen = Math.hypot(edgeVec.x, edgeVec.y);
    if (edgeLen < 1e-6) return;
    const dir = normalizeVec(edgeVec);
    const inward = scaleVec(edgeOutwardNormal(p1, p2, centroid), -1);
    for (let d = stallD; d + stallW <= edgeLen - stallD; d += stallW) {
      if (results.length >= MAX_ROAD_DIRECT_SLOTS) break;
      const edgeMidPt = addP(p1, scaleVec(dir, d + stallW / 2));
      const cellCenter = addP(edgeMidPt, scaleVec(inward, clearance + stallD / 2));
      const angle = Math.atan2(dir.y, dir.x);
      const candidate: ParkingSlot = {
        center: cellCenter,
        width: stallW,
        depth: stallD,
        angle,
        type: 'normal',
        roadEdgeIndex: i,
      };
      if (isCellValidRoadDirect(ctx, candidate, results, zone, excludeRects))
        results.push(candidate);
    }
  });
  return results;
}

/* --- 壁背負い配置: 敷地境界・建物の壁を背にして1列(手前に通路)を配置し、壁のない中央部だけ背合わせにする --- */
export function packStallsWallBacked(
  ctx: ParkingContext,
  zone: OrientedRect | null,
  excludeRects: OrientedRect[],
  slide = false
): { stalls: ParkingSlot[]; lanes: ParkingLane[] } {
  const pts = ctx.sitePts;
  const stalls: ParkingSlot[] = [];
  const lanes: ParkingLane[] = [];
  if (pts.length < 3) return { stalls, lanes };
  const centroid = polygonCentroid(pts);
  // 各マスの手前(通路側)の帯。角で直交する別の列のマスが、先に置いた列の通路をふさがないようにするために使う
  const stallHash = new RectSpatialHash(10);
  const aisleHash = new RectSpatialHash(10);
  const buildingRects: OrientedRect[] = ctx.buildings.map((b) => ({
    center: b.center,
    width: b.width,
    depth: b.depth,
    angle: b.angle,
  }));
  const segments: { p1: Point; p2: Point; isBuilding: boolean; ref?: Building }[] = [];
  for (let i = 0; i < pts.length; i++)
    segments.push({ p1: pts[i], p2: pts[(i + 1) % pts.length], isBuilding: false });
  ctx.buildings.forEach((b) => {
    const c = orientedRectCorners(b);
    for (let i = 0; i < 4; i++)
      segments.push({ p1: c[i], p2: c[(i + 1) % 4], isBuilding: true, ref: b });
  });
  segments.forEach((seg) => {
    const { p1, p2, isBuilding, ref } = seg;
    const edgeVec = subP(p2, p1);
    const edgeLen = Math.hypot(edgeVec.x, edgeVec.y);
    if (edgeLen < ctx.stallW) return;
    const dir = normalizeVec(edgeVec);
    const inward =
      isBuilding && ref
        ? edgeOutwardNormal(p1, p2, ref.center)
        : scaleVec(edgeOutwardNormal(p1, p2, centroid), -1);
    const wallOffset = isBuilding ? ctx.buildingClearance || 0 : ctx.siteClearance || 0;
    const rowEntries: number[] = [];
    // slide=true: 置けなかった位置はマス幅ぶん丸ごと飛ばさず、細かいステップでずらして次に置ける位置を探す
    // (固定ピッチだと建物角の離隔や他列とのわずかな干渉でも1台分の空きが残るため)
    const slideStep = slide ? ctx.cellSize || 0.5 : ctx.stallW;
    for (let d = 0; d + ctx.stallW <= edgeLen; ) {
      const edgeMidPt = addP(p1, scaleVec(dir, d + ctx.stallW / 2));
      const cellCenter = addP(edgeMidPt, scaleVec(inward, wallOffset + ctx.stallD / 2));
      const angle = Math.atan2(dir.y, dir.x);
      const candidate: ParkingSlot = {
        center: cellCenter,
        width: ctx.stallW,
        depth: ctx.stallD,
        angle,
        type: 'normal',
      };
      const aisleFootprint: OrientedRect = {
        center: addP(cellCenter, scaleVec(inward, ctx.stallD / 2 + ctx.aisleW / 2)),
        width: ctx.stallW,
        depth: ctx.aisleW,
        angle,
      };
      // マス自体の妥当性に加え、(1)他の列の通路帯に入らない (2)自分の手前の通路帯が他のマス・建物・敷地外にかからない
      // ことを確認する。角付近では直交する2列が互いの通路をふさぎ、奥のマスに出入りできなくなるため
      const valid =
        isCellValidRoadDirect(ctx, candidate, stalls, zone, excludeRects) &&
        !aisleHash.anyOverlap(candidate) &&
        !stallHash.anyOverlap(aisleFootprint) &&
        !buildingRects.some((b) => rectsOverlapSAT(aisleFootprint, b)) &&
        orientedRectCorners(aisleFootprint).every((c) => pointInPolygon(c, pts));
      if (valid) {
        stalls.push(candidate);
        stallHash.add(candidate);
        aisleHash.add(aisleFootprint);
        rowEntries.push(d);
        d += ctx.stallW;
      } else {
        d += slideStep;
      }
    }
    if (rowEntries.length > 0) {
      const dMin = Math.min(...rowEntries);
      const dMax = Math.max(...rowEntries) + ctx.stallW;
      const rowCenterAlong = addP(p1, scaleVec(dir, (dMin + dMax) / 2));
      const rowCenter = addP(rowCenterAlong, scaleVec(inward, wallOffset + ctx.stallD / 2));
      const rowAngle = Math.atan2(dir.y, dir.x);
      lanes.push({
        center: rowCenter,
        width: dMax - dMin,
        depth: ctx.stallD,
        angle: rowAngle,
        aisleW: ctx.aisleW,
        laneType: 'wall',
        inward,
        // 列が途切れている箇所には通路も要らない(他の列のマスがそこに入り得る)ため、連続区間ごとに通路帯を作る
        aisles: contiguousRuns(rowEntries, ctx.stallW).map(([a, b]) => ({
          center: addP(
            addP(p1, scaleVec(dir, (a + b) / 2)),
            scaleVec(inward, wallOffset + ctx.stallD + ctx.aisleW / 2)
          ),
          width: b - a,
          depth: ctx.aisleW,
          angle: rowAngle,
        })),
      });
    }
  });
  return { stalls, lanes };
}

/* --- 角度ソルバー --- */
export function getRowsAlongXOverride(ctx: ParkingContext): boolean | undefined {
  if (ctx.rowsAlongXMode === 'rows') return true;
  if (ctx.rowsAlongXMode === 'cols') return false;
  return undefined;
}

type PackFn = (
  grid: FreeGrid,
  stallW: number,
  stallD: number,
  aisleW: number,
  rowsAlongXOverride: boolean | undefined
) => PackedResult;
interface SolvedLayout {
  baseAngleRad: number;
  offsetDeg: number;
  angleRad: number;
  grid: FreeGrid;
  packed: PackedResult;
  isEdgeAligned: boolean;
}
export function solveLayoutAngle(
  ctx: ParkingContext,
  packFn: PackFn,
  packArgs: [number, number, number, boolean | undefined],
  zone: OrientedRect | null,
  excludeRects: OrientedRect[]
): SolvedLayout | null {
  return solveLayoutAngleCandidates(ctx, packFn, packArgs, zone, excludeRects).best;
}
/** solveLayoutAngleと同じ探索を行い、選ばれた角度(best)に加えて全候補も返す(呼び出し側で別の指標で選び直せるように) */
export function solveLayoutAngleCandidates(
  ctx: ParkingContext,
  packFn: PackFn,
  packArgs: [number, number, number, boolean | undefined],
  zone: OrientedRect | null,
  excludeRects: OrientedRect[],
  hardRects?: OrientedRect[]
): { best: SolvedLayout | null; candidates: SolvedLayout[] } {
  const baseAngleRad = computeSiteMainAxisAngleRad(ctx.sitePts);
  let offsets: number[];
  let edgeAligned: number[] = [];
  if (ctx.angleMode === 'manual') {
    offsets = [ctx.manualAngleDeg || 0];
  } else {
    edgeAligned = computeEdgeAlignedOffsets(ctx.sitePts, baseAngleRad);
    const merged = [...edgeAligned, ...PARKING_AUTO_ANGLE_SWEEP_DEG];
    offsets = merged.filter((d, i) => merged.findIndex((x) => Math.abs(x - d) < 1) === i);
  }
  let best: SolvedLayout | null = null;
  const candidates: SolvedLayout[] = [];
  for (const offsetDeg of offsets) {
    const angleRad = baseAngleRad + toRad(offsetDeg);
    const grid = buildFreeGrid(ctx, angleRad, { restrictRect: zone, excludeRects, hardRects });
    const packed = packFn(grid, ...packArgs);
    const isEdgeAligned = edgeAligned.some((a) => Math.abs(a - offsetDeg) < 1);
    candidates.push({ baseAngleRad, offsetDeg, angleRad, grid, packed, isEdgeAligned });
    if (!best || packed.stalls.length > best.packed.stalls.length) {
      best = candidates[candidates.length - 1];
    }
  }
  if (best && !best.isEdgeAligned) {
    const near = candidates.filter(
      (c) =>
        c.isEdgeAligned && c.packed.stalls.length >= (best as SolvedLayout).packed.stalls.length - 1
    );
    if (near.length) {
      best = near.reduce((a, b) => (b.packed.stalls.length > a.packed.stalls.length ? b : a));
    }
  }
  return { best, candidates };
}

export function siteBoundingZone(ctx: ParkingContext, angleRad: number): OrientedRect | null {
  if (ctx.sitePts.length < 3) return null;
  const localPts = ctx.sitePts.map((p) => rotP(p, -angleRad));
  const bb = bbox(localPts);
  if (bb.minX >= bb.maxX || bb.minY >= bb.maxY) return null;
  return localAxisRectToWorldOriented(bb.minX, bb.minY, bb.maxX, bb.maxY, angleRad, 0);
}

/* --- 配置方式ごとのゾーン管理: 有効化時にゾーンが未設定なら、他ゾーンと重ならない範囲を初期配置する --- */
export function ensureMethodZoneDefaults(
  ctx: ParkingContext,
  methods: Record<ParkingMethodKey, ParkingMethodState>
): void {
  const baseAngleRad = computeSiteMainAxisAngleRad(ctx.sitePts);
  const zone = siteBoundingZone(ctx, baseAngleRad);
  const cloneZone = () =>
    zone
      ? { center: { ...zone.center }, width: zone.width, depth: zone.depth, angle: zone.angle }
      : null;
  PARKING_METHOD_KEYS.forEach((key) => {
    const m = methods[key];
    if (key === 'backToBack') {
      // 背合わせの領域は常に敷地領域と一致させる(地図上の個別ゾーン表示・手動調整は行わない)
      m.zone = cloneZone();
      return;
    }
    if (!m.enabled || m.zone) return;
    m.zone = cloneZone();
  });
}

/* --- 出入口に接続する通路領域(敷地の道路辺の出入口から敷地内側へ、最寄りの配置ゾーンまで) --- */
export function computeDrivewayConnectors(ctx: ParkingContext): DrivewayConnector[] {
  const pts = ctx.sitePts;
  const edges = ctx.siteEdges;
  if (pts.length < 3) return [];
  const centroid = polygonCentroid(pts);
  // 通路(出入口の接続路)は、敷地内の駐車マス間の通路幅と揃える(最大数確保のため過剰に確保しない)
  const targetDepth = Math.max(ctx.aisleW || 5.5, 2);
  const connectors: DrivewayConnector[] = [];
  edges.forEach((edge, i) => {
    if (edge.type !== 'road' || !Array.isArray(edge.driveways)) return;
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    const perpInward = scaleVec(edgeOutwardNormal(p1, p2, centroid), -1);
    edge.driveways.forEach((dw) => {
      const inward = rotP(perpInward, toRad(dw.angleDeg || 0));
      const entryPt = addP(p1, scaleVec(subP(p2, p1), dw.position));
      // 長さ指定がある出入口は、その長さで固定する(敷地外・建物にかかる手前で打ち切る)
      const fixedLength = typeof dw.lengthM === 'number' && dw.lengthM > 0;
      const maxDepth = fixedLength ? (dw.lengthM as number) : targetDepth;
      let depth = maxDepth;
      for (let d = 0.5; d <= maxDepth; d += 0.5) {
        const probe = addP(entryPt, scaleVec(inward, d));
        const blocked =
          !pointInPolygon(probe, pts) ||
          (fixedLength && ctx.buildings.some((b) => insideBuildingWithMargin(probe, b, 0)));
        if (blocked) {
          depth = Math.max(0.5, d - 0.5);
          break;
        }
        depth = d;
      }
      // 注意: orientedRectCorners(rect)は「width=angle方向(ここではinward=敷地内側)の長さ、
      // depth=それと垂直な方向の長さ」として解釈するため、ここでは
      // width=敷地内部へ伸びる奥行き、depth=出入口自体の幅(道路辺に沿った長さ)という、
      // 直感とは逆の割り当てになっている
      connectors.push({
        center: addP(entryPt, scaleVec(inward, depth / 2)),
        width: depth,
        depth: Math.max(2, dw.widthM),
        angle: Math.atan2(inward.y, inward.x),
        edgeIndex: i,
        drivewayId: dw.id,
        fixedLength,
      });
    });
  });
  return connectors;
}
/* 出入口の通路を、実際に生成された駐車レーン(通路+マス帯)まで伸ばして接続する(見た目・動線上のつながりを確保) */
const CONNECTOR_MAX_EXTEND_M = 40;
const CONNECTOR_MIN_VISUAL_DEPTH_M = 10;
export function extendConnectorsToLanes(
  ctx: ParkingContext,
  connectors: DrivewayConnector[],
  lanes: ParkingLane[],
  slots: ParkingSlot[] = []
): void {
  connectors.forEach((c) => {
    if (c.fixedLength) return;
    const inward = rotP({ x: 1, y: 0 }, c.angle);
    const entryPt = subP(c.center, scaleVec(inward, c.width / 2));
    let depth = c.width;
    const reach: OrientedRect = {
      center: addP(entryPt, scaleVec(inward, CONNECTOR_MAX_EXTEND_M / 2)),
      width: CONNECTOR_MAX_EXTEND_M,
      depth: c.depth,
      angle: c.angle,
    };
    const nearSlots = nearbyRects(reach, slots, 0);
    for (let d = c.width; d <= CONNECTOR_MAX_EXTEND_M; d += 0.5) {
      const probe = addP(entryPt, scaleVec(inward, d));
      if (!pointInPolygon(probe, ctx.sitePts)) break;
      if (ctx.buildings.some((b) => insideBuildingWithMargin(probe, b, 0))) break;
      // 通路をマスの上に通さない(レーンに属さない残地補填のマスもあるため、マス自体とも判定する)
      const extended: OrientedRect = {
        center: addP(entryPt, scaleVec(inward, d / 2)),
        width: d,
        depth: c.depth,
        angle: c.angle,
      };
      if (nearSlots.some((sl) => rectsOverlapSAT(extended, sl))) break;
      depth = d;
      const longEnoughToRead = d >= CONNECTOR_MIN_VISUAL_DEPTH_M;
      if (longEnoughToRead && lanes.some((l) => insideBuildingWithMargin(probe, l, 0))) break;
    }
    if (depth > c.width) {
      c.center = addP(entryPt, scaleVec(inward, depth / 2));
      c.width = depth;
    }
  });
}

export function specialCounts(
  parking: Pick<ParkingState, 'disabledCount' | 'guestCount' | 'evCount'>
): {
  disabled: number;
  guest: number;
  ev: number;
} {
  return { disabled: parking.disabledCount, guest: parking.guestCount, ev: parking.evCount };
}

export function excludeRectsFor(
  occupiedRects: OrientedRect[],
  drivewayConnectors: DrivewayConnector[],
  bicycle: BicycleState
): OrientedRect[] {
  const rects = [...occupiedRects];
  if (bicycle.enabled && !bicycle.autoZone && bicycle.zone) rects.push(bicycle.zone);
  if (Array.isArray(drivewayConnectors)) rects.push(...drivewayConnectors);
  return rects;
}

export function finalizeBicycleZoneFromGrid(
  bicycle: BicycleState,
  grid: FreeGrid,
  angleRad: number
): { zone: OrientedRect | null; capacity: number } {
  if (!bicycle.enabled) return { zone: null, capacity: 0 };
  if (!bicycle.autoZone && bicycle.zone) {
    const area = bicycle.zone.width * bicycle.zone.depth;
    return { zone: bicycle.zone, capacity: Math.floor(area / (bicycle.unitArea || 0.6)) };
  }
  const localZone = findMaxFreeRect(grid);
  const zone = localZone
    ? localAxisRectToWorldOriented(
        localZone.x0,
        localZone.y0,
        localZone.x1,
        localZone.y1,
        angleRad,
        0
      )
    : null;
  const zoneArea = localZone ? (localZone.x1 - localZone.x0) * (localZone.y1 - localZone.y0) : 0;
  const capacity = localZone ? Math.floor(zoneArea / (bicycle.unitArea || 0.6)) : 0;
  return { zone, capacity };
}

/* --- 配置方式ごとのマス生成(各ゾーン範囲に限定) --- */
function finalizeSolvedForMethod(
  m: ParkingMethodState,
  solved: SolvedLayout | null
): { slots: ParkingSlot[]; lanes: ParkingLane[] } {
  if (!solved?.packed || solved.packed.stalls.length === 0) {
    m.baseAxisAngleDeg = solved ? toDeg(solved.baseAngleRad) : null;
    m.solvedOffsetDeg = null;
    m.solvedAngleDeg = null;
    m.solvedRowsAlongX = null;
    m.capacity = 0;
    return { slots: [], lanes: [] };
  }
  const extraAngle = solved.packed.rowsAlongX ? 0 : Math.PI / 2;
  const worldSlots = solved.packed.stalls.map((s) =>
    localSlotToWorldOriented(s, solved.angleRad, extraAngle, solved.packed.rowsAlongX)
  );
  const worldLanes = solved.packed.lanes.map((l) =>
    localLaneToWorldOriented(l, solved.angleRad, extraAngle, solved.packed.rowsAlongX)
  );
  m.baseAxisAngleDeg = toDeg(solved.baseAngleRad);
  m.solvedOffsetDeg = solved.offsetDeg;
  m.solvedAngleDeg = toDeg(solved.angleRad);
  m.solvedRowsAlongX = solved.packed.rowsAlongX;
  m.capacity = worldSlots.length;
  return { slots: worldSlots, lanes: worldLanes };
}
// 矩形の外接円半径。重なり判定の前に、明らかに離れている矩形を安価に除外するために使う
function boundingRadius(r: OrientedRect): number {
  return Math.hypot(r.width, r.depth) / 2;
}
function nearbyRects<T extends OrientedRect>(target: OrientedRect, rects: T[], pad: number): T[] {
  const rt = boundingRadius(target) + pad;
  return rects.filter(
    (r) =>
      Math.hypot(r.center.x - target.center.x, r.center.y - target.center.y) <=
      rt + boundingRadius(r)
  );
}
// 外接円のバウンディングボックスで登録する簡易空間ハッシュ。多数の矩形との重なり判定を近傍だけに絞る
class RectSpatialHash {
  private readonly buckets = new Map<string, OrientedRect[]>();
  constructor(private readonly size: number) {}
  private keysFor(r: OrientedRect): string[] {
    const rad = boundingRadius(r);
    const x0 = Math.floor((r.center.x - rad) / this.size);
    const x1 = Math.floor((r.center.x + rad) / this.size);
    const y0 = Math.floor((r.center.y - rad) / this.size);
    const y1 = Math.floor((r.center.y + rad) / this.size);
    const keys: string[] = [];
    for (let ix = x0; ix <= x1; ix++) for (let iy = y0; iy <= y1; iy++) keys.push(`${ix},${iy}`);
    return keys;
  }
  add(r: OrientedRect): void {
    for (const k of this.keysFor(r)) {
      const b = this.buckets.get(k);
      if (b) b.push(r);
      else this.buckets.set(k, [r]);
    }
  }
  anyOverlap(r: OrientedRect): boolean {
    const seen = new Set<OrientedRect>();
    for (const k of this.keysFor(r)) {
      for (const o of this.buckets.get(k) || []) {
        if (seen.has(o)) continue;
        seen.add(o);
        if (rectsOverlapSAT(r, o)) return true;
      }
    }
    return false;
  }
}

/* --- 残地の補填: 既存の通路帯に面した空きに、通路へ向けた1列のマスを追加する --- */
// 背合わせ(奥行=マス2つ分+通路)の単位に満たない残地(奥行がマス1つ分以上あれば)も、既存通路を共用して使えるようにする
export function fillStallsAlongAisles(
  ctx: ParkingContext,
  zone: OrientedRect | null,
  excludeRects: OrientedRect[],
  existingSlots: ParkingSlot[],
  lanes: ParkingLane[],
  extraAisles: OrientedRect[] = []
): ParkingSlot[] {
  const aisles = [...lanes.flatMap(laneAisleRects), ...extraAisles];
  const clearance = ctx.buildingClearance || 0;
  const buildingRects: OrientedRect[] = ctx.buildings.map((b) => ({
    center: b.center,
    width: b.width + clearance * 2,
    depth: b.depth + clearance * 2,
    angle: b.angle,
  }));
  const siteClearance = ctx.siteClearance || 0;
  const step = ctx.cellSize || 0.5;
  const blockers = new RectSpatialHash(10);
  for (const r of [...existingSlots, ...aisles, ...excludeRects, ...buildingRects]) blockers.add(r);
  const placed: ParkingSlot[] = [];
  const isValid = (c: ParkingSlot): boolean => {
    if (blockers.anyOverlap(c)) return false;
    const corners = orientedRectCorners(c);
    for (const p of corners) {
      if (!pointInPolygon(p, ctx.sitePts)) return false;
      if (siteClearance > 0 && distToPolygon(p, ctx.sitePts) < siteClearance - 1e-6) return false;
      if (zone && !insideBuildingWithMargin(p, zone, 1e-6)) return false;
    }
    return true;
  };
  for (const aisle of aisles) {
    for (const side of [-1, 1]) {
      const offsetY = side * (aisle.depth / 2 + ctx.stallD / 2);
      for (let u = -aisle.width / 2; u + ctx.stallW <= aisle.width / 2 + 1e-6; ) {
        const center = addP(aisle.center, rotP({ x: u + ctx.stallW / 2, y: offsetY }, aisle.angle));
        const candidate: ParkingSlot = {
          center,
          width: ctx.stallW,
          depth: ctx.stallD,
          angle: aisle.angle,
          type: 'normal',
          tandemGroupId: null,
          tandemPosition: null,
          roadEdgeIndex: null,
        };
        if (isValid(candidate)) {
          placed.push(candidate);
          blockers.add(candidate);
          u += ctx.stallW;
        } else {
          u += step;
        }
      }
    }
  }
  return placed;
}
// 中央部の背合わせ列のうち、前の通路が壁背負い列のマスや建物にふさがれるマスを除き、通路区間を作り直す。
// グリッドの近似(セル単位の判定)で見逃す干渉を、実際の矩形同士の判定で最終確認するためのもの
function dropBlockedAisleStalls(
  ctx: ParkingContext,
  slots: ParkingSlot[],
  lanes: ParkingLane[],
  obstacles: RectSpatialHash
): ParkingSlot[] {
  const removed = new Set<ParkingSlot>();
  for (const lane of lanes) {
    if (lane.laneType !== 'pair' || !lane.aisles || lane.aisles.length === 0) continue;
    if (!lane.aisles.some((a) => obstacles.anyOverlap(a))) continue;
    const dir = rotP({ x: 1, y: 0 }, lane.angle);
    const along = (p: Point) => (p.x - lane.center.x) * dir.x + (p.y - lane.center.y) * dir.y;
    // 通路帯を列の手前側/奥側に分け、それぞれ面しているマスを判定し直す
    const sides = new Map<number, { toAisle: Point; segs: OrientedRect[] }>();
    for (const a of lane.aisles) {
      const offset = subP(a.center, lane.center);
      const toAisle = subP(offset, scaleVec(dir, offset.x * dir.x + offset.y * dir.y));
      const key = Math.sign(toAisle.x * -dir.y + toAisle.y * dir.x);
      const side = sides.get(key);
      if (side) side.segs.push(a);
      else sides.set(key, { toAisle, segs: [a] });
    }
    const rebuilt: OrientedRect[] = [];
    for (const { toAisle, segs } of sides.values()) {
      const unit = normalizeVec(toAisle);
      const footprintOf = (s: ParkingSlot): OrientedRect => ({
        center: addP(s.center, scaleVec(unit, ctx.stallD / 2 + lane.aisleW / 2)),
        width: ctx.stallW,
        depth: lane.aisleW,
        angle: lane.angle,
      });
      if (!segs.some((a) => obstacles.anyOverlap(a))) {
        rebuilt.push(...segs);
        continue;
      }
      const facing = slots.filter(
        (s) =>
          !removed.has(s) &&
          segs.some((a) => insideBuildingWithMargin(footprintOf(s).center, a, 0.01))
      );
      const kept: ParkingSlot[] = [];
      for (const s of facing) {
        if (obstacles.anyOverlap(footprintOf(s))) removed.add(s);
        else kept.push(s);
      }
      rebuilt.push(
        ...contiguousRuns(
          kept.map((s) => along(s.center) - ctx.stallW / 2),
          ctx.stallW
        ).map(([u0, u1]) => ({
          center: addP(addP(lane.center, scaleVec(dir, (u0 + u1) / 2)), toAisle),
          width: u1 - u0,
          depth: lane.aisleW,
          angle: lane.angle,
        }))
      );
    }
    lane.aisles = rebuilt;
  }
  return removed.size ? slots.filter((s) => !removed.has(s)) : slots;
}

// 中央部の角度候補のうち、中央部単体の台数で上位いくつかを、壁背負い列+残地補填まで含めた合計台数で比較し直す数
const HYBRID_RERANK_TOP_K = 4;
// マスを除いた後に残る、面するマスのない通路帯・マスのないレーンを取り除く。
// レーンは複数の案で共有しているため、書き換えずに新しいオブジェクトを作る
function pruneLanesToSlots(lanes: ParkingLane[], slots: ParkingSlot[]): ParkingLane[] {
  const faces = (s: ParkingSlot, a: OrientedRect) =>
    [-1, 1].some((side) => {
      const probe = addP(s.center, rotP({ x: 0, y: side * (s.depth / 2 + a.depth / 2) }, s.angle));
      return insideBuildingWithMargin(probe, a, 0.05);
    });
  const result: ParkingLane[] = [];
  for (const lane of lanes) {
    if (lane.isCorridor) {
      result.push(lane);
      continue;
    }
    const inLane = slots.filter((s) => insideBuildingWithMargin(s.center, lane, 0.05));
    if (inLane.length === 0) continue;
    if (!lane.aisles) {
      result.push(lane);
      continue;
    }
    result.push({ ...lane, aisles: lane.aisles.filter((a) => inLane.some((s) => faces(s, a))) });
  }
  return result;
}
// 到達できないマスへの通り道を確保して配置し直す試行の最大回数
const CORRIDOR_PLAN_ROUNDS = 2;
interface AccessContext {
  base: AccessBase;
  entries: OrientedRect[];
}
// 残地補填のマスのうち、それを置くことで他のマスへ行けなくなる(通り道をふさぐ)ものを見つける。
// 1台ずつ試すと遅いため、まとめて試してふさぐ場合だけ半分に分けて絞り込む(グループテスト)
function findBlockingStalls(
  candidates: ParkingSlot[],
  blocksSomething: (subset: ParkingSlot[]) => boolean
): ParkingSlot[] {
  if (candidates.length === 0 || !blocksSomething(candidates)) return [];
  if (candidates.length === 1) return candidates;
  const mid = candidates.length >> 1;
  const found = [
    ...findBlockingStalls(candidates.slice(0, mid), blocksSomething),
    ...findBlockingStalls(candidates.slice(mid), blocksSomething),
  ];
  const rest = candidates.filter((c) => !found.includes(c));
  // 単独ではふさがないが組み合わせでふさぐ場合は、安全側でこのグループをまとめて除外する
  return blocksSomething(rest) ? candidates : found;
}
function computeWallBackedHybrid(
  ctx: ParkingContext,
  zone: OrientedRect,
  excludeRects: OrientedRect[],
  slide: boolean,
  access: AccessContext | null = null,
  corridors: OrientedRect[] = []
): {
  slots: ParkingSlot[];
  lanes: ParkingLane[];
  solved: SolvedLayout | null;
  /** 出入口からたどり着けずに除いたマス(通り道を計画する手がかりにする) */
  dropped: ParkingSlot[];
} {
  // 1段目: 敷地境界・建物の壁を背にした1列を優先配置(通路は手前1本だけで済み、狭い残地も使える)
  const wallResult = packStallsWallBacked(ctx, zone, excludeRects, slide);
  const wallOccupied = wallResult.lanes.length
    ? wallResult.lanes.map(laneToOccupiedRect)
    : wallResult.stalls;
  const interiorExcludeRects = [...excludeRects, ...wallOccupied];
  const wallSlots = wallResult.stalls.map((s) => ({
    ...s,
    tandemGroupId: null,
    tandemPosition: null,
    roadEdgeIndex: null,
  }));
  // 2段目: 壁のない中央部の残りは背合わせ(2列+両側通路)で埋める
  const { best, candidates } = solveLayoutAngleCandidates(
    ctx,
    packStallsBackToBack,
    [ctx.stallW, ctx.stallD, ctx.aisleW, getRowsAlongXOverride(ctx)],
    zone,
    interiorExcludeRects,
    // 背合わせ列の通路は壁背負い列の通路とは共用できるが、壁背負い列のマスの上には取れない
    wallSlots
  );
  const wallHash = new RectSpatialHash(10);
  for (const w of wallSlots) wallHash.add(w);
  // 背合わせ列の手前の通路をふさいではいけないもの(壁背負い列のマスと建物)
  const obstacleHash = new RectSpatialHash(10);
  for (const w of wallSlots) obstacleHash.add(w);
  for (const b of ctx.buildings)
    obstacleHash.add({ center: b.center, width: b.width, depth: b.depth, angle: b.angle });
  const assemble = (solved: SolvedLayout | null) => {
    // finalizeSolvedForMethodはmを書き換えるため、ここでは使い捨てのmで変換だけ行う
    const scratch = { capacity: 0 } as ParkingMethodState;
    const interior = finalizeSolvedForMethod(scratch, solved);
    // グリッド近似の境界(斜め方向の交差など)で稀に生じるはみ出しを、幾何学的なSAT判定で最終チェックして除外する
    const safeInteriorSlots = dropBlockedAisleStalls(
      ctx,
      interior.slots.filter((s) => !wallHash.anyOverlap(s)),
      interior.lanes,
      obstacleHash
    );
    // 確保した通り道も通路として表示し、残地補填ではその脇にもマスを置けるようにする
    const corridorLanes: ParkingLane[] = corridors.map((c) => ({
      ...c,
      laneType: 'single',
      aisleW: c.depth,
      aisles: [c],
      isCorridor: true,
    }));
    const lanes = [...wallResult.lanes, ...interior.lanes, ...corridorLanes];
    const baseSlots = [...wallSlots, ...safeInteriorSlots];
    // 3段目: 背合わせの単位に満たない残地を、既存通路に面した1列で埋める
    let fillSlots = fillStallsAlongAisles(ctx, zone, excludeRects, baseSlots, lanes);
    if (!access) return { slots: [...baseSlots, ...fillSlots], lanes, solved, dropped: [] };
    // 4段目: 車が通れる幅を考慮した到達確認。残地補填のマスが他のマスへの通り道をふさぐなら置かず、
    // 最後に出入口からたどり着けないマスを除く
    const before = computeAccess(access.base, baseSlots, access.entries);
    const reachableBefore = baseSlots.filter(before.accessible);
    const blocks = (subset: ParkingSlot[]) => {
      const after = computeAccess(access.base, [...baseSlots, ...subset], access.entries);
      return reachableBefore.some((s) => !after.accessible(s));
    };
    const blocking = findBlockingStalls(fillSlots, blocks);
    if (blocking.length) fillSlots = fillSlots.filter((f) => !blocking.includes(f));
    let all = [...baseSlots, ...fillSlots];
    // 出入口からたどり着けないマスがあれば、その手前で通り道をふさいでいるマスを取り除いて通り道を開ける
    const opened = openCorridors(access.base, all, access.entries);
    if (opened.length) all = all.filter((s) => !opened.includes(s));
    const final = computeAccess(access.base, all, access.entries);
    const kept = all.filter(final.accessible);
    const dropped = all.filter((s) => !final.accessible(s));
    return { slots: kept, lanes: pruneLanesToSlots(lanes, kept), solved, dropped };
  };
  // 中央部の台数だけで選んだ角度が、壁背負い列・残地補填を含めた合計でも最多とは限らない
  // (通路幅を変えると別角度が中央部だけ僅差で上回り、合計では減るといった逆転が起きる)ため、上位候補を合計で比べ直す
  const bestInterior = best ? best.packed.stalls.length : 0;
  const ranked = [...candidates]
    .filter((c) => c !== best && c.packed.stalls.length >= bestInterior * 0.9)
    .sort((a, b) => b.packed.stalls.length - a.packed.stalls.length)
    .slice(0, HYBRID_RERANK_TOP_K - 1);
  let result = assemble(best);
  for (const c of ranked) {
    const r = assemble(c);
    if (r.slots.length > result.slots.length) result = r;
  }
  return result;
}
function computeMethodSlots(
  ctx: ParkingContext,
  key: ParkingMethodKey,
  m: ParkingMethodState,
  occupiedRects: OrientedRect[],
  drivewayConnectors: DrivewayConnector[],
  bicycle: BicycleState
): { slots: ParkingSlot[]; lanes: ParkingLane[] } {
  if (!m.enabled || !m.zone) {
    m.capacity = 0;
    return { slots: [], lanes: [] };
  }
  const excludeRects = excludeRectsFor(occupiedRects, drivewayConnectors, bicycle);
  if (key === 'roadDirect') {
    const worldSlots = packStallsRoadDirect(ctx, m.zone, excludeRects);
    worldSlots.forEach((s) => {
      s.tandemGroupId = null;
      s.tandemPosition = null;
    });
    const angleRad = computeSiteMainAxisAngleRad(ctx.sitePts);
    m.baseAxisAngleDeg = toDeg(angleRad);
    m.solvedOffsetDeg = 0;
    m.solvedAngleDeg = toDeg(angleRad);
    m.solvedRowsAlongX = null;
    m.capacity = worldSlots.length;
    return { slots: worldSlots, lanes: [] };
  }
  if (key === 'backToBack') {
    // 出入口から通路で到達できるマスの数(レイアウト案の比較指標)。
    // 単純な台数比較だと、通路がつながらないマスの多い配置を選んでしまうことがある
    const reachableCount = (r: { slots: ParkingSlot[]; lanes: ParkingLane[] }): number => {
      const connectors = drivewayConnectors.map((c) => ({ ...c, center: { ...c.center } }));
      extendConnectorsToLanes(ctx, connectors, r.lanes, r.slots);
      return (
        r.slots.length - computeCirculationWarning(r.lanes, connectors, r.slots).disconnectedCount
      );
    };
    const zone = m.zone;
    // 到達確認の下準備(敷地・建物のマスク)は全案で共通なので一度だけ作る。出入口がなければ確認しない
    const access: AccessContext | null = drivewayConnectors.length
      ? { base: buildAccessBase(ctx.sitePts, ctx.buildings), entries: drivewayConnectors }
      : null;
    const run = (aisleW: number, slide: boolean) => {
      const r = computeWallBackedHybrid({ ...ctx, aisleW }, zone, excludeRects, slide, access);
      return { ...r, aisleW, slide, score: reachableCount(r) };
    };
    // 1) 標準の通路幅で、壁背負い列の固定ピッチ/スライド配置の両方を試す
    // (スライドは空きを詰められるが、ずれた位置のマスが中央部の背合わせ列を妨げて逆に減る場合もあるため)
    const fixed = run(ctx.aisleW, false);
    const slid = run(ctx.aisleW, true);
    let best = slid.score > fixed.score ? slid : fixed;
    // 2) 通路が原因でマスが入らない場合に備え、最小通路幅まで狭めた案も試す。
    // 台数が増えるときだけ採用し、増えるなら中間の幅でも同じ台数になるか確かめて、できるだけ広い通路を残す
    const minW = Math.min(ctx.aisleMinW || ctx.aisleW, ctx.aisleW);
    if (minW < ctx.aisleW - 1e-6) {
      const narrow = run(minW, best.slide);
      if (narrow.score > best.score) {
        best = narrow;
        const midW = Math.round(((ctx.aisleW + minW) / 2) * 10) / 10;
        if (midW > minW + 1e-6 && midW < ctx.aisleW - 1e-6) {
          const mid = run(midW, best.slide);
          if (mid.score >= best.score) best = mid;
        }
      }
    }
    // 3) 出入口からたどり着けずに除いたマスがあれば、そこまでの通り道を先に確保して配置し直す。
    // 通り道はマスを置かない範囲として扱い、その脇には残地補填でマスを置ける。台数が増えるときだけ採用する
    if (access) {
      const corridors: OrientedRect[] = [];
      for (let round = 0; round < CORRIDOR_PLAN_ROUNDS && best.dropped.length; round++) {
        const combined = [...best.slots, ...best.dropped];
        const acc = computeAccess(access.base, combined, access.entries);
        if (!acc.reached || !acc.probeCells) break;
        const targets = best.dropped.flatMap(acc.probeCells);
        const planned = planCorridor(access.base, acc.reached, targets, best.aisleW);
        if (!planned) break;
        const trial = [...corridors, ...planned];
        const r = computeWallBackedHybrid(
          { ...ctx, aisleW: best.aisleW },
          zone,
          [...excludeRects, ...trial],
          best.slide,
          access,
          trial
        );
        if (r.slots.length <= best.slots.length) break;
        corridors.push(...planned);
        best = { ...r, aisleW: best.aisleW, slide: best.slide, score: reachableCount(r) };
      }
    }
    finalizeSolvedForMethod(m, best.solved);
    m.solvedAisleW = best.aisleW;
    // 台数が減っても、壁・敷地境界を背にする配置を常に優先する(施工性・見た目の自然さを重視)
    m.capacity = best.slots.length;
    return { slots: best.slots, lanes: best.lanes };
  }
  const packFn = key === 'tandem' ? packStallsTandem : packStallsBackToBack;
  return finalizeSolvedForMethod(
    m,
    solveLayoutAngle(
      ctx,
      packFn,
      [ctx.stallW, ctx.stallD, ctx.aisleW, getRowsAlongXOverride(ctx)],
      m.zone,
      excludeRects
    )
  );
}

// 出入口から車が通れる幅でたどり着けないマスの数(道路直接駐車は道路から出入りするため対象外)と、
// それを含むレーン。出入口がない場合は判定しない
function computeAccessWarning(
  ctx: ParkingContext,
  lanes: ParkingLane[],
  connectors: DrivewayConnector[],
  slots: ParkingSlot[]
): CirculationWarning {
  if (!connectors.length) return { disconnectedLaneIndices: [], disconnectedCount: 0 };
  const result = computeAccess(buildAccessBase(ctx.sitePts, ctx.buildings), slots, connectors);
  const unreachable = slots.filter((s) => s.method !== 'roadDirect' && !result.accessible(s));
  const disconnectedLaneIndices = lanes
    .map((_, i) => i)
    .filter((i) => unreachable.some((s) => insideBuildingWithMargin(s.center, lanes[i], 0.3)));
  return { disconnectedLaneIndices, disconnectedCount: unreachable.length };
}

export interface ParkingEngineInput {
  site: SiteState;
  buildings: Building[];
  parking: ParkingState;
  bicycle: BicycleState;
}

export interface ParkingEngineResult {
  generatedSlots: ParkingSlot[];
  lanes: ParkingLane[];
  capacity: number;
  drivewayConnectors: DrivewayConnector[];
  circulationWarning: CirculationWarning;
  methods: Record<ParkingMethodKey, ParkingMethodState>;
  bicycle: { zone: OrientedRect | null; capacity: number };
}

function cloneMethods(
  methods: Record<ParkingMethodKey, ParkingMethodState>
): Record<ParkingMethodKey, ParkingMethodState> {
  const result = {} as Record<ParkingMethodKey, ParkingMethodState>;
  PARKING_METHOD_KEYS.forEach((key) => {
    result[key] = { ...methods[key] };
  });
  return result;
}

function emptyResult(methods: Record<ParkingMethodKey, ParkingMethodState>): ParkingEngineResult {
  const clearedMethods = {} as Record<ParkingMethodKey, ParkingMethodState>;
  PARKING_METHOD_KEYS.forEach((key) => {
    const m = methods[key];
    clearedMethods[key] = {
      enabled: m.enabled,
      zone: m.zone,
      baseAxisAngleDeg: null,
      solvedOffsetDeg: null,
      solvedAngleDeg: null,
      solvedRowsAlongX: null,
      capacity: 0,
    };
  });
  return {
    generatedSlots: [],
    lanes: [],
    capacity: 0,
    drivewayConnectors: [],
    circulationWarning: { disconnectedLaneIndices: [], disconnectedCount: 0 },
    methods: clearedMethods,
    bicycle: { zone: null, capacity: 0 },
  };
}

export function recomputeParking(input: ParkingEngineInput): ParkingEngineResult {
  const methods = cloneMethods(input.parking.methods);
  try {
    if (!input.site.origin || input.site.points.length < 3) {
      return emptyResult(methods);
    }
    const ctx = buildContext(input);
    ensureMethodZoneDefaults(ctx, methods);
    const drivewayConnectors = computeDrivewayConnectors(ctx);
    const allSlots: ParkingSlot[] = [];
    const allLanes: ParkingLane[] = [];
    const occupiedRects: OrientedRect[] = [];
    PARKING_METHOD_KEYS.forEach((key) => {
      const { slots, lanes } = computeMethodSlots(
        ctx,
        key,
        methods[key],
        occupiedRects,
        drivewayConnectors,
        input.bicycle
      );
      slots.forEach((s) => {
        s.method = key;
      });
      lanes.forEach((l) => {
        l.method = key;
      });
      allSlots.push(...slots);
      allLanes.push(...lanes);
      // レーンに属さないマス(残地補填の1列など)もあるため、マス自体も常に占有扱いにする
      occupiedRects.push(...lanes.map(laneToOccupiedRect), ...slots);
    });
    extendConnectorsToLanes(ctx, drivewayConnectors, allLanes, allSlots);
    assignSpecialSlots(
      allSlots.filter((s) => s.tandemPosition !== 'back'),
      specialCounts(input.parking)
    );
    const capacity = allSlots.length;
    const circulationWarning = computeAccessWarning(ctx, allLanes, drivewayConnectors, allSlots);

    const angleRad = computeSiteMainAxisAngleRad(ctx.sitePts);
    const zoneRects = PARKING_METHOD_KEYS.map((k) => methods[k])
      .filter((m) => m.enabled && m.zone)
      .map((m) => m.zone as OrientedRect)
      .concat(drivewayConnectors);
    const grid = buildFreeGrid(ctx, angleRad, { excludeRects: zoneRects });
    allSlots.forEach((s) => {
      const corners = orientedRectCorners(s).map((c) => rotP(c, -angleRad));
      const xs = corners.map((c) => c.x);
      const ys = corners.map((c) => c.y);
      markRectOccupied(grid, Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys));
    });
    rebuildPrefixSum(grid);
    const bicycleResult = finalizeBicycleZoneFromGrid(input.bicycle, grid, angleRad);

    return {
      generatedSlots: allSlots,
      lanes: allLanes,
      capacity,
      drivewayConnectors,
      circulationWarning,
      methods,
      bicycle: bicycleResult,
    };
  } catch {
    return emptyResult(methods);
  }
}
